import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { amountDecimals, amountLocale, currency, money } from "../../utils/currency";
import { copy } from "../../copy";
import { selectKioskLanguage } from "../../kiosk/language/selectors";
import { getLoyaltyProviderForDiscount } from "../../helpers/loyaltyHelper";
import { saveSaleData } from "../../redux-store/slices/SaleSlice";
import { getErrorMessage } from "../../utils/posErrors";
import {
  applyLoyaltyPoints,
  clearLoyaltyDetails,
  fetchLoyaltyDetails,
  removeLoyaltyPoints,
  setRedemptionProvider,
} from "../index";
import { buildRedemptionTooltip, getProgram } from "../program";
import { REDEMPTION_FAILED_KEY, RELEASE_FAILED_KEY } from "../constants";
import {
  canApplyPoints,
  clampPointsInput,
  classifyRedeemResponse,
  getRedemptionLimits,
  getRedemptionPanelState,
  parsePoints,
  pointsToAmount,
} from "../redemption";
import {
  selectActiveLoyaltyProvider,
  selectActiveProviderLoyaltyDetails,
  selectCustomerLoyaltyInfo,
  selectLoyaltyDiscount,
  selectLoyaltyProgramDetails,
  selectLoyaltyProviders,
  selectSaleData,
} from "../selectors";
import { useCollectibleLabel } from "./useCollectibleLabel";
import type { RedemptionPanelState } from "../types";

/**
 * The whole points-redemption panel, headless.
 *
 * A view renders `state.kind` and wires the callbacks; it holds no loyalty state of its own and
 * does no arithmetic. Everything below is either a selector read, a pure rule, or an adapter
 * call — which is what lets the POS panel, the tablet layout, the kiosk and mobile share one
 * implementation and differ only in markup.
 *
 * ```tsx
 * const loyalty = useLoyaltyRedemption();
 * if (loyalty.state.kind === "available") …
 * ```
 *
 * The redemption itself is a two-step conversation: `applyMax` / `applyPartial` redeem, and IF
 * the backend asks for an OTP `otpSession.isOpen` turns true and `useRedeemPointsOtp` finishes
 * it. When it does not, the points are simply applied.
 */
export function useLoyaltyRedemption() {
  const dispatch = useDispatch();

  /**
   * 🔴 **The currency deps for every memo below that formats money.** `money` is a module
   * function: its IDENTITY never changes while its OUTPUT is resolved from the store per call, so
   * listing the function alone gives a dep that can never invalidate anything and a `tooltip`
   * frozen with whatever symbol, precision and grouping were live when it was first built.
   * Reading the three here, at render, is what makes those memos track the business — and it is what the
   * `currencySymbol` this hook used to expose was quietly doing before it became `money`.
   */
  const currencySymbol = currency();
  const decimals = amountDecimals();
  // The THIRD merchant property `money()` resolves, since 2026-09-19. Same argument as the
  // two above — a formatted string built under one grouping locale does not re-render itself.
  const grouping = amountLocale();
  // The LANGUAGE, for the same reason as the three above: `tooltip` below holds a FORMATTED
  // sentence, and `copy` reads the language from the store rather than being named in the
  // callback — so without this the tooltip keeps whatever language it was first built in.
  const language = useSelector(selectKioskLanguage);

  const collectibleLabel = useCollectibleLabel();

  const activeProvider = useSelector(selectActiveLoyaltyProvider);
  const details = useSelector(selectActiveProviderLoyaltyDetails);
  const programDetails = useSelector(selectLoyaltyProgramDetails);
  const appliedDiscount = useSelector(selectLoyaltyDiscount);
  const loyaltyProviders = useSelector(selectLoyaltyProviders);
  const saleData = useSelector(selectSaleData);
  const customerLoyaltyInfo = useSelector(selectCustomerLoyaltyInfo);
  const isBlocked = Boolean(customerLoyaltyInfo?.isBlocked);

  const program = getProgram(programDetails);
  const context = useMemo(
    () => ({ providerMetaId: activeProvider?.metaId, details, program, sale: saleData }),
    [activeProvider?.metaId, details, program, saleData],
  );

  const limits = useMemo(() => getRedemptionLimits(context), [context]);
  const state: RedemptionPanelState = useMemo(
    () =>
      getRedemptionPanelState({
        ...context,
        hasActiveProvider: !!activeProvider,
        isBlocked,
        appliedDiscount,
        programDetails,
      }),
    [context, activeProvider, isBlocked, appliedDiscount, programDetails],
  );

  const [isPartialMode, setIsPartialMode] = useState(false);
  const [pointsInput, setPointsInputRaw] = useState("");
  const [pendingPoints, setPendingPoints] = useState(0);
  const [isOtpOpen, setIsOtpOpen] = useState(false);
  const [error, setError] = useState("");
  /**
   * A redeem or release is in flight.
   *
   * Both calls are a dispatch plus a callback with nothing in between, so without this the
   * panel looks identical before and after a press and a second press sends a second request
   * against the same sale.
   *
   * 🔴 It cannot stick — but NOT for the reason the sagas appear to give, and the difference
   * matters to anyone hardening this later.
   *
   * Every catch in `saga/loyalty` now calls `actions.payload?.callback?.()` and WOULD report —
   * the `.data.` typo that made half of them silent no-ops was fixed on 2026-09-08. But that
   * changes nothing here, because none of those catches is REACHABLE: `commonPostData` swallows
   * every error and returns `undefined` (`saga/loyalty/shared` says so in its own header). What
   * actually runs on a failure is the EMPTY-RESPONSE branch, and that one does call back — with
   * `undefined`. The guarantee rests there, and `loyaltyRefundHelper` wraps the release side
   * either way.
   *
   * ⚠️ So do not "harden" this by leaning on the catch: it is unreachable, not merely broken.
   * Making `commonPostData` rethrow is the change that would put the catches in play, and it
   * would need every caller's failure handling re-read first — the empty-response branch is
   * what all of them are written against.
   */
  const [isBusy, setIsBusy] = useState(false);
  /**
   * The same fact as `isBusy`, held where a GUARD can read it.
   *
   * 🔴 State cannot gate a second press on its own. `setIsBusy(true)` schedules a render; it does
   * not change `isBusy` for the handler already running, or for another handler that fires
   * before React commits. Two presses inside one batch therefore both read `false` and both
   * send a request against the same sale — which is exactly what this guard exists to stop.
   * React 18 flushes discrete events synchronously, so a human double-tap is usually safe; "the
   * scheduler usually saves us" is not the guarantee to hang a duplicate redemption on.
   *
   * A ref flips SYNCHRONOUSLY, so the second caller sees it. `isBusy` stays beside it purely to
   * render with — a ref change would not re-render, and the views need to disable their CTAs.
   * Always move the two together, through `beginRequest` / `endRequest`.
   */
  const inFlight = useRef(false);

  /**
   * Claim the in-flight slot. `false` means a request is already running and the caller must do
   * nothing at all.
   */
  const beginRequest = useCallback(() => {
    if (inFlight.current) return false;
    inFlight.current = true;
    setIsBusy(true);
    return true;
  }, []);

  /** Release it. Every path that calls `beginRequest` must reach this from its callback. */
  const endRequest = useCallback(() => {
    inFlight.current = false;
    setIsBusy(false);
  }, []);

  /** The field follows the ceiling until the cashier edits it. */
  useEffect(() => {
    setPointsInputRaw(limits.maxPoints ? String(limits.maxPoints) : "0");
  }, [limits.maxPoints]);

  /**
   * Keep the slice's provider in step with the active one — the redeem/verify payloads are built
   * from the slice, not from this hook's arguments.
   *
   * ⚠️ The teardown clears the ENTIRE balance cache, matching the panel's long-standing
   * behaviour. It is why the panel refetches on every mount.
   */
  useEffect(() => {
    if (activeProvider) setRedemptionProvider(activeProvider);
    return () => clearLoyaltyDetails();
  }, [activeProvider]);

  /** Nothing cached for this provider yet — ask. The answer is per-sale, so it is not global. */
  useEffect(() => {
    if (activeProvider && !details) fetchLoyaltyDetails(activeProvider);
  }, [activeProvider, details]);

  const previewAmount = useMemo(
    () => pointsToAmount(parsePoints(pointsInput), context).redeemableAmount,
    [pointsInput, context],
  );

  const tooltip = useMemo(
    () => buildRedemptionTooltip(program, limits.maxAmount, money, copy),
    // The rule calls the two currency deps unnecessary because the callback does not NAME them —
    // `money` reads them from the store instead, which is exactly why they have to be listed.
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [program, limits.maxAmount, currencySymbol, decimals, grouping, language],
  );

  const setPointsInput = useCallback(
    (value: string) => setPointsInputRaw(clampPointsInput(value, limits.maxPoints)),
    [limits.maxPoints],
  );

  /**
   * Redeem. On success the returned sale is saved either way; an OTP challenge only decides
   * whether the conversation continues.
   */
  const redeem = useCallback(
    (points: number) => {
      if (!points || !beginRequest()) return;
      setError("");
      setPendingPoints(points);
      applyLoyaltyPoints(points, (res: any) => {
        endRequest();
        /**
         * 🔴 The response is classified by a PURE rule, not read field by field here.
         *
         * This block used to test `res?.code || res?.errorCode` and then reach straight for
         * `res.sale` — which threw on every failed request, because a request failure arrives
         * as `undefined` rather than as a code. The `TypeError` went back into the saga's own
         * `try`, whose catch discards it, so the customer got no discount, no message and
         * nothing in the log. `classifyRedeemResponse` is where "no sale means failed" now
         * lives, and it is tested with plain objects.
         */
        const outcome = classifyRedeemResponse(res);
        if (outcome.kind === "failed") {
          // An unrecognised code passes through `getErrorMessage` unchanged, and the
          // empty-response case has no code at all — so the fallback is what a customer
          // actually reads most of the time.
          setError(getErrorMessage(outcome.code) || copy(REDEMPTION_FAILED_KEY));
          return;
        }
        // Saved on BOTH non-failed outcomes: a challenge still carries the points on its sale,
        // and `useKioskPointsRedemption`'s `otp.cancel` depends on the discount being there.
        dispatch(saveSaleData(outcome.sale));
        if (outcome.kind === "challenge") setIsOtpOpen(true);
        else setIsPartialMode(false);
      });
    },
    [dispatch, beginRequest, endRequest],
  );

  const applyMax = useCallback(() => redeem(limits.maxPoints), [redeem, limits.maxPoints]);
  const applyPartial = useCallback(
    () => redeem(parsePoints(pointsInput)),
    [redeem, pointsInput],
  );

  const canApply = useMemo(
    () => canApplyPoints(parsePoints(pointsInput), context),
    [pointsInput, context],
  );

  /**
   * Release the applied points. The provider is resolved from the discount, not from context.
   *
   * 🔴 **A failure is REPORTED, and `onDone` does not run.** Both halves matter, and neither was
   * true before: the response was discarded and `onDone` fired either way.
   *
   * A failed refund leaves the discount on the bill on purpose — the backend still has the
   * points blocked, so local state agreeing with it is the invariant, not a bug
   * (`docs/flows/LOYALTY_REFUND.md`). The cost is that NOTHING VISIBLE CHANGES on a failure: the
   * panel still reads "applied", the kiosk's toggle stays on, and the customer has no way to
   * know their total did not move. `error` is the only channel that can say so — see
   * `RELEASE_FAILED_KEY` for why the helper's toast is not a substitute.
   *
   * ⚠️ `onDone` is what re-seeds the points field and opens or closes the partial editor, i.e.
   * it describes a bill that no longer has points on it. Running it on a failure told the panel
   * the release had happened: `changeAmount` in particular opened the partial editor UNDERNEATH
   * a summary still reading "applied", offering an edit of points that were never released.
   *
   * The failure test is `!res?.refundSuccess` — the same field `loyaltyRefundHelper` branches on
   * to decide whether to save the sale, so the two cannot disagree about what failed. It covers
   * every shape the saga can answer with: an explicit `refundSuccess: false`, the `undefined` a
   * swallowed request error produces, and the `{ code, status: false }` from its catch.
   */
  const removeApplied = useCallback(
    (onDone?: () => void) => {
      if (!beginRequest()) return;
      const provider: any = appliedDiscount
        ? getLoyaltyProviderForDiscount(loyaltyProviders as any, appliedDiscount as any)
        : null;
      // Cleared here rather than on success, so a message from the previous attempt cannot sit
      // over a release that is already in flight. A press refused by `beginRequest` clears
      // nothing, because it did not start an attempt.
      setError("");
      removeLoyaltyPoints(provider?.mid, (res: any) => {
        endRequest();
        if (!res?.refundSuccess) {
          setError(copy(RELEASE_FAILED_KEY));
          return;
        }
        onDone?.();
      });
    },
    [appliedDiscount, loyaltyProviders, beginRequest, endRequest],
  );

  /** "Remove" — release the points and leave the panel offering them again. */
  const remove = useCallback(
    () =>
      removeApplied(() => {
        setIsPartialMode(false);
        setPointsInputRaw(String(limits.maxPoints));
      }),
    [removeApplied, limits.maxPoints],
  );

  /** "Change amount" — release the points and reopen the editor seeded with what was applied. */
  const changeAmount = useCallback(() => {
    const applied = state.kind === "applied" ? state.appliedPoints : 0;
    removeApplied(() => {
      setIsPartialMode(true);
      setPointsInputRaw(String(applied));
    });
  }, [removeApplied, state]);

  const openPartial = useCallback(() => setIsPartialMode(true), []);
  const closePartial = useCallback(() => {
    setIsPartialMode(false);
    setPointsInputRaw(String(limits.maxPoints));
  }, [limits.maxPoints]);

  const closeOtp = useCallback(() => setIsOtpOpen(false), []);

  /** Called by the OTP step once the backend has accepted the code. */
  const onOtpVerified = useCallback(() => {
    setIsOtpOpen(false);
    setIsPartialMode(false);
  }, []);

  /**
   * MEMOISED, and that is load-bearing rather than tidy.
   *
   * Rebuilt on every render this object was a new reference each time, so every consumer memo
   * keyed on it recomputed too — `useKioskPointsRedemption` wraps `cancelOtp` in a `useCallback`
   * on `[otpSession, remove]` and its `otp` in a `useMemo`, and BOTH were dead: the deps changed
   * every render, so the memo never once returned a cached value.
   *
   * Nothing was visibly broken, which is why it survived — a memo that never hits is invisible
   * until something downstream depends on a stable identity (an effect, a `React.memo` child)
   * and then re-runs forever. `closeOtp` and `onOtpVerified` are already stable, so the only
   * real inputs are the two values.
   */
  const otpSession = useMemo(
    () => ({ isOpen: isOtpOpen, points: pendingPoints, close: closeOtp, onVerified: onOtpVerified }),
    [isOtpOpen, pendingPoints, closeOtp, onOtpVerified],
  );

  return {
    /** What to render. Switch on `.kind`. */
    state,
    limits,
    program,
    programDetails,
    details,
    activeProvider,
    collectibleLabel,
    /**
     * The panel's money formatter — symbol AND the merchant's precision, bound together so a
     * caller cannot print one without the other. This replaced a bare `currencySymbol`, which is
     * how every line of the panel came to read `₹120` beside a cart reading `₹120.00`.
     *
     * Core's own module function, so its identity is stable and it needs no memo.
     */
    money,
    /** "You can redeem up to…" — empty string when the programme promises nothing. */
    tooltip,
    /** Error from the last redemption attempt, already turned into a message. */
    error,
    /**
     * A redeem or release is in flight — disable the CTAs on it.
     *
     * Added for the kiosk, where a customer taps a control that has not visibly moved yet, but
     * the double-submit it prevents was always reachable from the POS panel too.
     */
    isBusy,

    /** Partial-points editor. */
    isPartialMode,
    openPartial,
    closePartial,
    pointsInput,
    setPointsInput,
    previewAmount,
    canApply,

    /** Actions. */
    applyMax,
    applyPartial,
    remove,
    changeAmount,

    /** OTP step — hand `points` to `useRedeemPointsOtp`. */
    otpSession,
  };
}
